import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { BrandLogo } from "../components/BrandLogo";
import { ArrowLeft, ArrowRight, ShieldCheck, Eye, AlertTriangle, LifeBuoy, BadgeCheck, CheckCircle2 } from "lucide-react";
import { HONOLULU_GUIDES } from "../lib/honoluluGuides";

const PAGE_PATH = "/first-date-safety-tips";
const PAGE_URL = "https://datebid.org/first-date-safety-tips";
const PAGE_TITLE = "First Date Safety Tips | DateBid";
const PAGE_DESC = "Simple, practical first date safety tips: meet in public, tell a friend, protect your details, and trust your instincts. Meet with confidence.";
const OG_IMAGE = "https://datebid.org/brand/og-datebid.png";

const BEFORE = [
  "Verify what you can. Look for a Verified badge, and have a short chat first so you get a sense of the person.",
  "Choose a public place. Cafes, restaurants and busy parks are good choices.",
  "Tell a friend. Share who, where and when, and agree to check in afterward.",
];
const DURING = [
  "Arrange your own transport. You decide when to leave.",
  "Keep personal details private. Don't share your home address, workplace details or financial information.",
  "Watch your drink and your pace. Stay aware, and keep it moderate.",
  "Trust your instincts. If something feels off, it's fine to leave.",
];
const REDFLAGS = [
  "Pressure to move to a private location",
  "Requests for money or financial details",
  "Refusal to meet in public",
  "Inconsistent stories or evasive answers",
];

const Bullets = ({ items, testid }) => (
  <ul className="space-y-2 text-sm text-[#D8D2C9] leading-relaxed pl-1" data-testid={testid}>
    {items.map((t, i) => (<li key={i} className="flex gap-2"><CheckCircle2 size={16} className="text-[#D4AF37] mt-0.5 shrink-0" /><span>{t}</span></li>))}
  </ul>
);

export default function FirstDateSafetyTips() {
  useEffect(() => {
    document.title = PAGE_TITLE;
    const upsert = (sel, create) => { let el = document.head.querySelector(sel); if (!el) { el = create(); document.head.appendChild(el); } return el; };
    const setName = (n, c) => { const e = upsert(`meta[name="${n}"]`, () => { const m = document.createElement("meta"); m.setAttribute("name", n); return m; }); e.setAttribute("content", c); };
    const setProp = (p, c) => { const e = upsert(`meta[property="${p}"]`, () => { const m = document.createElement("meta"); m.setAttribute("property", p); return m; }); e.setAttribute("content", c); };
    setName("description", PAGE_DESC);
    upsert('link[rel="canonical"]', () => { const l = document.createElement("link"); l.setAttribute("rel", "canonical"); return l; }).setAttribute("href", PAGE_URL);
    setProp("og:type", "article"); setProp("og:site_name", "DateBid"); setProp("og:url", PAGE_URL);
    setProp("og:title", PAGE_TITLE); setProp("og:description", PAGE_DESC); setProp("og:image", OG_IMAGE);
    setName("twitter:card", "summary_large_image"); setName("twitter:title", PAGE_TITLE); setName("twitter:description", PAGE_DESC); setName("twitter:image", OG_IMAGE);
    const ld = document.createElement("script");
    ld.type = "application/ld+json"; ld.id = "ld-first-date-safety-tips";
    ld.text = JSON.stringify({ "@context": "https://schema.org", "@type": "Article", "headline": "First Date Safety Tips", "description": PAGE_DESC, "mainEntityOfPage": { "@type": "WebPage", "@id": PAGE_URL }, "image": OG_IMAGE, "author": { "@type": "Organization", "name": "DateBid" }, "publisher": { "@type": "Organization", "name": "DateBid", "logo": { "@type": "ImageObject", "url": OG_IMAGE } } });
    const ex = document.getElementById("ld-first-date-safety-tips"); if (ex) ex.remove();
    document.head.appendChild(ld);
    return () => { const s = document.getElementById("ld-first-date-safety-tips"); if (s) s.remove(); };
  }, []);

  return (
    <div className="db-grain min-h-screen relative" style={{ background: "#0C0A0A" }} data-testid="safety-tips-page">
      <nav className="relative z-20 flex items-center justify-between px-6 sm:px-10 py-5">
        <Link to="/" data-testid="tips-brand-logo" className="inline-flex items-center"><BrandLogo variant="wordmark" className="h-10 sm:h-12 w-auto" /></Link>
        <Link to="/" data-testid="tips-back-link" className="text-sm text-[#D8D2C9] hover:text-[#FBF8F3] inline-flex items-center gap-1.5"><ArrowLeft size={15} /> Back to home</Link>
      </nav>

      <header className="relative z-10 px-6 sm:px-10 pt-6 pb-10 max-w-3xl mx-auto">
        <div className="text-[11px] uppercase tracking-[0.3em] text-[#D4AF37] mb-4">Dating · Safety</div>
        <h1 className="font-serif text-4xl sm:text-5xl text-[#FBF8F3] leading-tight">First Date Safety Tips</h1>
        <p className="mt-5 text-[#D8D2C9] leading-relaxed">Meeting someone new should feel exciting, not stressful. These habits help you enjoy the evening while staying in control.</p>
      </header>

      <main className="relative z-10 px-6 sm:px-10 pb-20 max-w-3xl mx-auto space-y-10">
        <section data-testid="tips-before">
          <div className="flex items-center gap-3 mb-3"><div className="h-10 w-10 rounded-full bg-[#541224]/70 border border-[#D4AF37]/40 flex items-center justify-center text-[#D4AF37] shrink-0"><ShieldCheck size={18} /></div><h2 className="font-serif text-2xl text-[#FBF8F3]">Before you meet</h2></div>
          <Bullets items={BEFORE} testid="tips-before-list" />
        </section>
        <section data-testid="tips-during">
          <div className="flex items-center gap-3 mb-3"><div className="h-10 w-10 rounded-full bg-[#541224]/70 border border-[#D4AF37]/40 flex items-center justify-center text-[#D4AF37] shrink-0"><Eye size={18} /></div><h2 className="font-serif text-2xl text-[#FBF8F3]">During the date</h2></div>
          <Bullets items={DURING} testid="tips-during-list" />
        </section>
        <section data-testid="tips-redflags">
          <div className="flex items-center gap-3 mb-3"><div className="h-10 w-10 rounded-full bg-[#541224]/70 border border-[#D4AF37]/40 flex items-center justify-center text-[#D4AF37] shrink-0"><AlertTriangle size={18} /></div><h2 className="font-serif text-2xl text-[#FBF8F3]">Red flags</h2></div>
          <Bullets items={REDFLAGS} testid="tips-redflags-list" />
        </section>
        <section data-testid="tips-ifwrong">
          <div className="flex items-center gap-3 mb-3"><div className="h-10 w-10 rounded-full bg-[#541224]/70 border border-[#D4AF37]/40 flex items-center justify-center text-[#D4AF37] shrink-0"><LifeBuoy size={18} /></div><h2 className="font-serif text-2xl text-[#FBF8F3]">If something goes wrong</h2></div>
          <p className="text-[#D8D2C9] text-sm leading-relaxed pl-1">Leave, contact a friend, and report the person to the platform. In an emergency, call 911.</p>
        </section>

        <section data-testid="tips-datebid" className="rounded-3xl border border-[#D4AF37]/20 bg-gradient-to-br from-[#541224]/40 to-[#141113]/70 p-8 sm:p-10">
          <div className="flex items-center gap-3 mb-3"><div className="h-10 w-10 rounded-full bg-[#541224]/70 border border-[#D4AF37]/40 flex items-center justify-center text-[#D4AF37] shrink-0"><BadgeCheck size={18} /></div><h2 className="font-serif text-2xl sm:text-3xl text-[#FBF8F3] leading-tight">How DateBid helps</h2></div>
          <p className="mt-2 text-[#D8D2C9] text-sm leading-relaxed">Members must be 18+. You can submit identity verification, and a Verified badge appears only after approval. You can block or report anyone instantly, and DateBid will never ask you to send money through the app.</p>
          <Link to="/auth?mode=register" data-testid="tips-join-btn" className="db-offer-btn rounded-full px-7 py-3.5 text-sm font-semibold inline-flex items-center gap-2 mt-6">Join DateBid <ArrowRight size={16} /></Link>
        </section>

        <section data-testid="tips-related" className="pt-6 border-t border-white/8">
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#D4AF37] mb-4">Keep reading</div>
          <div className="space-y-2">
            {HONOLULU_GUIDES.filter((g) => g.path !== PAGE_PATH).map((g, i) => (
              <Link key={g.path} to={g.path} data-testid={`tips-related-${i + 1}`} className="flex items-center gap-1.5 text-sm font-semibold text-[#D4AF37] hover:text-[#E6C468] transition-colors">{g.label} <ArrowRight size={14} /></Link>
            ))}
          </div>
        </section>
      </main>

      <footer className="relative z-10 px-6 sm:px-10 py-10 border-t border-white/8 text-center text-xs text-[#9E978E]">
        <p>DateBid · Meet in public places · Never share home addresses or financial details · Members must be 18+</p>
        <p className="mt-1" data-testid="tips-safety-disclaimer">General safety advice, not a guarantee.</p>
        <BrandLogo variant="wordmarkSm" className="h-9 w-auto mx-auto mt-3 opacity-90" />
      </footer>
    </div>
  );
}
